import jsPDF from 'jspdf';
import type { EirsaliyeRow, EirsaliyeItemRow } from './eirsaliye-service';

/**
 * Generate a combined daily PDF report for all e-Irsaliyes on a given date.
 * Uses jsPDF (already installed in the project).
 */
export function generateDailyReport(
  date: string,
  irsaliyes: EirsaliyeRow[],
  items: Array<EirsaliyeItemRow & { irsaliye_no: string }>,
) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  // ── Helper functions ──

  const addNewPageIfNeeded = (requiredSpace: number) => {
    if (y + requiredSpace > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      y = margin;
    }
  };

  const drawLine = () => {
    doc.setDrawColor(200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 3;
  };

  // ── Header ──
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Gunluk e-Irsaliye Raporu', margin, y);
  y += 8;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`Tarih: ${formatDate(date)}`, margin, y);
  y += 6;
  doc.text(`Olusturma: ${new Date().toLocaleString('tr-TR')}`, margin, y);
  y += 8;

  drawLine();

  // ── Summary ──
  const totalAdet = irsaliyes.reduce((sum, r) => sum + (r.toplam_adet || 0), 0);
  const uniqueProducts = new Set(items.map(i => i.malzeme_no).filter(Boolean)).size;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Ozet', margin, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);

  const summaryItems = [
    ['Toplam Irsaliye', String(irsaliyes.length)],
    ['Toplam Adet', String(totalAdet)],
    ['Benzersiz Urun', String(uniqueProducts)],
  ];

  for (const [label, value] of summaryItems) {
    doc.text(`${label}:`, margin, y);
    doc.setFont('helvetica', 'bold');
    doc.text(value, margin + 45, y);
    doc.setFont('helvetica', 'normal');
    y += 5;
  }

  y += 4;
  drawLine();

  // ── Irsaliyes Table ──
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Irsaliyeler', margin, y);
  y += 6;

  // Table header
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, y - 3, contentWidth, 6, 'F');

  const irsaliyeCols = [
    { x: margin + 1, w: 35, label: 'Irsaliye No' },
    { x: margin + 37, w: 25, label: 'ERP No' },
    { x: margin + 63, w: 20, label: 'Bayi Kodu' },
    { x: margin + 84, w: 40, label: 'Sube' },
    { x: margin + 125, w: 15, label: 'Adet' },
    { x: margin + 141, w: 25, label: 'Sevk Tarihi' },
  ];

  for (const col of irsaliyeCols) {
    doc.text(col.label, col.x, y);
  }
  y += 5;

  // Table rows
  doc.setFont('helvetica', 'normal');
  for (const irs of irsaliyes) {
    addNewPageIfNeeded(6);

    const rowData = [
      irs.irsaliye_no || '-',
      irs.erp_irsaliye_no || '-',
      irs.bayi_kodu || '-',
      (irs.sube || '-').substring(0, 25),
      String(irs.toplam_adet || 0),
      irs.sevk_tarihi ? formatDate(irs.sevk_tarihi) : '-',
    ];

    for (let i = 0; i < irsaliyeCols.length; i++) {
      doc.text(rowData[i], irsaliyeCols[i].x, y);
    }
    y += 5;
  }

  y += 4;
  drawLine();

  // ── Combined Line Items Table ──
  addNewPageIfNeeded(20);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Tum Kalemler', margin, y);
  y += 6;

  // Table header
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, y - 3, contentWidth, 6, 'F');

  const itemCols = [
    { x: margin + 1, w: 30, label: 'Irsaliye No' },
    { x: margin + 32, w: 25, label: 'Malzeme No' },
    { x: margin + 58, w: 75, label: 'Malzeme Tanimi' },
    { x: margin + 134, w: 15, label: 'Miktar' },
    { x: margin + 150, w: 15, label: 'Birim' },
  ];

  for (const col of itemCols) {
    doc.text(col.label, col.x, y);
  }
  y += 5;

  // Table rows
  doc.setFont('helvetica', 'normal');
  for (const item of items) {
    addNewPageIfNeeded(6);

    const rowData = [
      (item.irsaliye_no || '-').substring(0, 20),
      item.malzeme_no || '-',
      (item.malzeme_tanimi || '-').substring(0, 45),
      String(item.miktar),
      item.olcu_birimi || 'Adet',
    ];

    for (let i = 0; i < itemCols.length; i++) {
      doc.text(rowData[i], itemCols[i].x, y);
    }
    y += 5;
  }

  // ── Footer ──
  y += 6;
  drawLine();
  doc.setFontSize(8);
  doc.setTextColor(128);
  doc.text('Bu rapor MusteriX tarafindan otomatik olusturulmustur.', margin, y);

  // ── Download ──
  doc.save(`e-irsaliye-rapor-${date}.pdf`);
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '-';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}.${parts[1]}.${parts[0]}`;
  }
  return dateStr;
}
